import React, { cloneElement, isValidElement } from 'react';
import { Search } from 'lucide-react';

/**
 * Shell sidebar, from Fluid Functionalism's layouts.
 * Floating lifts the rail onto a higher surface. Inset leaves the rail flat
 * and lifts the page instead. Width is 256px. Rows are 32px.
 *
 * `render` on a row swaps the button for the caller's element (a link,
 * usually). That path clones the element instead of going through Base UI,
 * which would add role="button" to a link.
 */

export function Sidebar({ variant = 'floating', className, children, ...props }) {
  return (
    <div
      {...props}
      className={['edl-sidebar', className].filter(Boolean).join(' ')}
      data-variant={variant}
    >
      {children}
    </div>
  );
}

export function SidebarRail({ className, children }) {
  return (
    <div className={['edl-sidebar__rail', className].filter(Boolean).join(' ')}>
      <div className="edl-sidebar__panel">{children}</div>
    </div>
  );
}

export function SidebarInset({ className, children, ...props }) {
  return (
    <div {...props} className={['edl-sidebar__inset', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export function SidebarHeader({ className, children }) {
  return (
    <div className={['edl-sidebar__header', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export function SidebarContent({ label = 'Sidebar', className, children }) {
  return (
    <nav aria-label={label} className={['edl-sidebar__content', className].filter(Boolean).join(' ')}>
      {children}
    </nav>
  );
}

export function SidebarFooter({ className, children }) {
  return (
    <div className={['edl-sidebar__footer', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export function SidebarGroup({ className, children }) {
  return (
    <div className={['edl-sidebar__group', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

export function SidebarGroupLabel({ children }) {
  return <div className="edl-sidebar__label">{children}</div>;
}

export function SidebarMenu({ children }) {
  return <ul className="edl-sidebar__menu">{children}</ul>;
}

export function SidebarMenuItem({ children }) {
  return <li className="edl-sidebar__menu-item">{children}</li>;
}

export function SidebarMenuButton({
  active = false,
  icon: Icon,
  className,
  children,
  render,
  type,
  ...props
}) {
  const shared = {
    ...props,
    className: ['edl-sidebar__item', className].filter(Boolean).join(' '),
    'data-active': active ? '' : undefined,
    'aria-current': active ? 'page' : undefined,
  };
  const body = (
    <>
      {Icon ? <Icon size={16} strokeWidth={1.5} aria-hidden="true" /> : null}
      <span className="edl-sidebar__item-label">{children}</span>
    </>
  );

  if (isValidElement(render)) {
    return cloneElement(render, {
      ...shared,
      className: [shared.className, render.props.className].filter(Boolean).join(' '),
    }, body);
  }

  return (
    <button {...shared} type={type ?? 'button'}>
      {body}
    </button>
  );
}

export function SidebarAccount({ mark, trailingIcon: TrailingIcon, className, children, ...props }) {
  return (
    <button
      {...props}
      type="button"
      className={['edl-sidebar__item', 'edl-sidebar__account', className].filter(Boolean).join(' ')}
    >
      {mark ? <span className="edl-sidebar__mark" aria-hidden="true">{mark}</span> : null}
      <span className="edl-sidebar__item-label">{children}</span>
      {TrailingIcon ? <TrailingIcon size={16} strokeWidth={1.5} aria-hidden="true" /> : null}
    </button>
  );
}

export function SidebarSearch({ placeholder = 'Search…', shortcut, className, ...props }) {
  return (
    <label className={['edl-sidebar__search', className].filter(Boolean).join(' ')}>
      <Search size={16} strokeWidth={1.5} aria-hidden="true" />
      <input type="search" placeholder={placeholder} {...props} />
      {shortcut ? <span className="edl-sidebar__shortcut" aria-hidden="true">{shortcut}</span> : null}
    </label>
  );
}

export function SidebarPage({ title, children }) {
  return (
    <>
      <header className="edl-sidebar__page-header">
        <h1>{title}</h1>
      </header>
      <div className="edl-sidebar__page">{children}</div>
    </>
  );
}
